import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { Layout } from './components/Layout/Layout';
import ProgressBar from '../../common/componentsV2/ProgressBar';
import { AncillariAutoFormStyles } from '../AncillariAutoProduct/styles/AncillariAutoFormFlow.styles';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import { ancillariAutoSchema, answersFieldsBySteps } from '../AncillariAutoProduct/AncillariAutoFormFlow.validations';
import {
  getBlockingFields,
  buildMissingFieldsMessage,
  COLLAPSED_PANEL_FIELDS_BY_STEP,
} from '../AncillariAutoProduct/AncillariAutoFormFlow.stepErrors';

import {
  modifyRequestDataBackoffice,
} from '../../services/axios-client/axeCommons';
import generateFiscalCode from '../../common/helpers/generateFiscalCode';
// import { generalAlertError } from '../../common/utils/alerts';
import {
  km_during_one_year, vehicle_plate_types, vehiclesAmountInFamily,
} from '../../common/constants';
import {
  ancillariChangeIndex,
  ancillariGetInitialData, ancillariGetVehicleModels, ancillariGetVehicleVersions, ancillariSubmitAnswers, resetAncillariForm,
} from '../../features/formsBackoffice/formsBackofficeActions';
import {
  ancillariAutoGetCustomerData,
} from '../../features/forms/formsActions';
import { V2FontImport } from '../../common/formInputsV2/V2Fonts';
import { FooterNav } from '../../common/formInputsV2/FooterNav.styles';
import { PrimaryButton, SecondaryButton } from '../../common/formInputsV2/Button.styles';
import BackofficeAncillariStepOne from './part/BackofficeAncillariStepOne';
import BackofficeAncillariStepTwo from './part/BackofficeAncillariStepTwo';
import BackofficeAncillariStepThree from './part/BackofficeAncillariStepThree';
import { sonnerErrorAlert } from '../../common/utils/sonnerAlerts';
import QuoteGenerateLoading from '../../common/componentsV2/elements/QuoteGenerateLoading';

function BackOfficeAncillari() {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestToken } = useParams();
  const [proceedButton, setProceedButton] = useState(true);
  const [showStepOneFields, setShowStepOneFields] = useState(false);

  const skipCheckGenderRef = useRef(null);

  const state = useSelector((store) => store.formsBackoffice.ancillari);
  const {
    loading,
    isLoadingVehicleModels,
    isLoadingVehicleVersions,
    states,
    index,
    brands,
    vehicleModels,
    vehicleVersions,
    municipalities,
    formData,
    dataPrivacy,
    loadingQuotes,
  } = state;
  const {
    register,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    getValues,
    watch,
    control,
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(ancillariAutoSchema),
    shouldFocusError: false,
    defaultValues: {
      product: 'ancillari_auto',
      source: 'greenia.it',
      policy_effective_date: '',
      privacies: [],
      name: '',
      surname: '',
      email: '',
      fiscal_code: '',
      address: '',
      house_number: '',
      postal_code: '',
      phone: '',
      gender: '',
      date_of_birth: '',
      country_of_birth_code: 'Z000',
      province_of_birth_code: null,
      commune_of_birth_code: null,
      born_abroad: false,
      residence_commune_code: '',
      residence_province_code: '',
      active_customer_id: null,
      existing_customer: false,
      business_name: '',
      vat_number: '',
      company_type: '',

      vehicle_plate: '',
      imm_vehicle_month: '',
      imm_vehicle_year: '',
      vehicle_brand_code: '',
      vehicle_model_code: '',
      vehicle_version_code: '',
      vehicle_am_code: '',
      other_power_supply: '9',
      vehicle_parking: '1',
      theft_protection_code: '4',
      vehicle_usage: '1',
      predicted_km: 10000,
      vehicle_purchased_month: '',
      vehicle_purchased_year: '',
      vehicles_owned: 2,
      tow_hook: '0',
      weight: 0,
      power: 0,
      complete_manually: false,
      is_old_plate: '',
      payment_frequency: '',
      vehicle_value: '',
      guarantees: [],
    },
  });

  const { fields, update, replace } = useFieldArray({
    name: 'guarantees',
    control,
  });

  // Constants
  const stepsQuote = ['Dati del Veicolo', 'Garanzie', 'Dati Personali'];

  const formDataFlow = watch();

  const [skipBrandCodeEffect, setSkipBrandCodeEffect] = useState(false);
  const vehicleBrandCodeEffectDependencies = useRef();
  const vehicleBrandCodeDeps = [
    formDataFlow.vehicle_brand_code,
    formDataFlow.imm_vehicle_year,
  ];

  useEffect(() => {
    if (skipBrandCodeEffect) {
      setSkipBrandCodeEffect(false);
      return;
    }

    if (vehicleBrandCodeEffectDependencies.current === undefined) {
      vehicleBrandCodeEffectDependencies.current = JSON.stringify(vehicleBrandCodeDeps);
      return;
    }

    if (
      vehicleBrandCodeEffectDependencies.current
      === JSON.stringify(vehicleBrandCodeDeps)
    ) {
      return;
    }

    vehicleBrandCodeEffectDependencies.current = JSON.stringify(vehicleBrandCodeDeps);
    setValue('vehicle_model_code', '');
  }, vehicleBrandCodeDeps);

  const [skipModelCodeEffect, setSkipModelCodeEffect] = useState(false);
  const vehicleModelCodeEffectDependencies = useRef();
  const vehicleModelCodeDeps = [formDataFlow.vehicle_model_code];

  useEffect(() => {
    if (skipModelCodeEffect) {
      setSkipModelCodeEffect(false);
      return;
    }

    if (vehicleModelCodeEffectDependencies.current === undefined) {
      vehicleModelCodeEffectDependencies.current = JSON.stringify(vehicleModelCodeDeps);
      return;
    }

    if (
      vehicleModelCodeEffectDependencies.current
      === JSON.stringify(vehicleModelCodeDeps)
    ) {
      return;
    }

    vehicleModelCodeEffectDependencies.current = JSON.stringify(vehicleModelCodeDeps);

    setValue('vehicle_version_code', null);
    setValue('vehicle_am_code', '');
  }, vehicleModelCodeDeps);

  const updateFormData = (data) => {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      setValue(key, data[key], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    trigger(keys);
  };

  const updateUserData = (data) => {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      setValue(key, data[key], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    trigger(keys);
  };

  const isValid = (name) => {
    if (errors[name]?.message !== undefined) {
      return false;
    }

    if (touchedFields[name] === undefined) {
      return false;
    }

    return true;
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector('.is-invalid, [aria-invalid="true"], .general-input-error, .invalid-feedback.d-block');
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  const privacyItemChangeHandler = (value, item) => {
    if (!value.target.checked) {
      const newArray = [...formDataFlow.privacies];
      const index = newArray.indexOf(item.id);
      newArray.splice(index, 1);
      updateFormData({
        privacies: newArray,
      });
      return;
    }
    const answersNewArray = [...formDataFlow.privacies];
    answersNewArray.push(item.id);
    updateFormData({
      privacies: answersNewArray,
    });
  };

  const getCustomerData = async (customerId) => {
    ancillariAutoGetCustomerData(customerId);
  };

  useEffect(() => {
    ancillariGetInitialData();

    return () => {
      resetAncillariForm();
    };
  }, []);

  useEffect(() => {
    if (
      location.state !== null
      && location.state.activeCustomerId !== undefined
    ) {
      getCustomerData(location.state.activeCustomerId);
    }
  }, []);

  const getFiscalCode = (birthDate) => {
    const d = birthDate.split('-');
    const fData = {
      name: formDataFlow.name,
      surname: formDataFlow.surname,
      gender: formDataFlow.gender,
      day: d[2],
      month: d[1],
      year: d[0],
    };
    if (formDataFlow.born_abroad) {
      fData.birthplace = states.filter(
        (s) => s.state_code === formDataFlow.country_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = 'EE';
    } else {
      fData.birthplace = municipalities.filter(
        (municipality) => municipality.cadastral_code === formDataFlow.commune_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = formDataFlow.province_of_birth_code;
    }
    return generateFiscalCode(fData);
  };

  const [showPrivacyErrors, setShowPrivacyErrors] = useState(false);

  const validateDataPrivacy = () => {
    let { error, message } = '';

    dataPrivacy.forEach((privacy) => {
      if (privacy.required === 1) {
        const userDataArray = { ...formDataFlow }; // make a separate copy of the object
        const index = userDataArray.privacies.indexOf(privacy.id);

        if (index < 0) {
          error = true;
          message = 'Tutti i campi sono obbligatori! Tutta la privacy con la stella deve essere controllata';
        }
      }
    });
    return { error, message };
  };

  const submitForm = () => {
    const { error, message } = validateDataPrivacy();

    if (error) {
      setShowPrivacyErrors(true);
      sonnerErrorAlert(message);
      scrollToFirstError();
      return;
    }

    const _userData = { ...formDataFlow };

    if (_userData.born_abroad && _userData.country_of_birth_code === 'Z000') {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Non puoi selezionare Italia come stato estero!');
      return;
    }

    if (
      _userData.born_abroad
      && (_userData.country_of_birth_code === null
        || _userData.country_of_birth_code === ''
        || _userData.country_of_birth_code == undefined)
    ) {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Devi selezionare uno stato estero!');
      return;
    }

    if (
      formDataFlow.date_of_birth !== ''
      && formDataFlow.date_of_birth !== null
    ) {
      _userData.date_of_birth = moment(formDataFlow.date_of_birth).format(
        'YYYY-MM-DD',
      );
    } else {
      _userData.date_of_birth = null;
    }
    _userData.policy_effective_date = moment(
      formDataFlow.policy_effective_date,
    ).format('YYYY-MM-DD');
    _userData.fiscal_code = formDataFlow.gender !== 'G'
      ? getFiscalCode(formDataFlow.date_of_birth)
      : null;
    delete _userData.existing_customer;
    delete _userData.complete_manually;
    delete _userData.persona_giuridica;
    ancillariSubmitAnswers(_userData, navigate, requestToken);
  };

  const prevButton = () => {
    if (index > 1) {
      ancillariChangeIndex(index - 1);
    }
  };

  const nextButton = async () => {
    const res = await trigger(answersFieldsBySteps[index - 1]);

    if (res !== true) {
      const stepFields = answersFieldsBySteps[index - 1];
      const blocking = getBlockingFields(errors, stepFields);

      // Step 2 is just the guarantees list — point straight at it.
      if (index === 2 && errors.guarantees) {
        window.scrollTo(0, 0);
        sonnerErrorAlert('Seleziona almeno una garanzia per continuare.');
        return;
      }

      // Auto-open the collapsed "Detagli" panel when a blocking field lives inside it.
      const panelFields = COLLAPSED_PANEL_FIELDS_BY_STEP[index] || [];
      if (index === 1 && blocking.some((field) => panelFields.includes(field))) {
        setShowStepOneFields(true);
      }

      sonnerErrorAlert(buildMissingFieldsMessage(blocking));
      scrollToFirstError();
      return;
    }

    if (index === 1) {
      const version = vehicleVersions.find((v) => v.AM_code === formDataFlow.vehicle_am_code);
      if (version) {
        // const currYear = new Date().getFullYear();
        // const versionYear = version.year;
        // const isMoreThan6YearsOld = currYear - versionYear > 6;
        // if (isMoreThan6YearsOld) {
        //   generalAlertError('Attenzione. I veicoli con età maggiore di 6 anni sono soggetti a riserva direzionale. Contatta il back office per completare quotazione!');
        //   return;
        // }
      }
    }

    if (index === 3) {
      if (
        formDataFlow.born_abroad
        && formDataFlow.country_of_birth_code === 'Z000'
      ) {
        window.scrollTo(0, 0);
        sonnerErrorAlert('Non puoi selezionare Italia come stato estero!');
        return;
      }

      if (
        formDataFlow.born_abroad
        && (formDataFlow.country_of_birth_code === null
          || formDataFlow.country_of_birth_code === ''
          || formDataFlow.country_of_birth_code == undefined)
      ) {
        window.scrollTo(0, 0);
        sonnerErrorAlert('Devi selezionare uno stato estero!');
        return;
      }
    }

    if (index === 3) {
      const validation = validateDataPrivacy();
      if (validation.error) {
        setShowPrivacyErrors(true);
        sonnerErrorAlert(validation.message);
        scrollToFirstError();
        return;
      }
    }

    if (index === 3) {
      submitForm();
      return;
    }

    ancillariChangeIndex(index + 1);
    setTimeout(() => window.scrollTo(0, 0), 500);
  };

  useEffect(() => {
    const triggerPlateValidation = async () => {
      await trigger('vehicle_plate', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      });
    };

    if (formDataFlow.vehicle_plate !== '') {
      triggerPlateValidation();
    }
  }, [formDataFlow.is_old_plate]);

  useEffect(() => {
    if (formDataFlow.vehicle_brand_code) {
      ancillariGetVehicleModels(
        formDataFlow.vehicle_brand_code,
        formDataFlow.imm_vehicle_year,
      );
    }
  }, [formDataFlow.vehicle_brand_code, formDataFlow.imm_vehicle_year]);

  useEffect(() => {
    if (
      formDataFlow.vehicle_brand_code
      && formDataFlow.vehicle_model_code
      && formDataFlow.imm_vehicle_year
    ) {
      ancillariGetVehicleVersions(
        formDataFlow.vehicle_model_code,
        formDataFlow.imm_vehicle_year,
      );
    }
  }, [
    formDataFlow.vehicle_brand_code,
    formDataFlow.vehicle_model_code,
    formDataFlow.imm_vehicle_year,
  ]);

  useEffect(() => {
    if (skipCheckGenderRef.current === true) {
      skipCheckGenderRef.current = false;
      return;
    }

    const triggerValidation = {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    };

    if (formDataFlow.gender === 'G') {
      setValue('date_of_birth', null);
      setValue('country_of_birth_code', null);
      setValue('province_of_birth_code', null);
      setValue('commune_of_birth_code', null);
      setValue('born_abroad', false);
      setValue('name', null);
      setValue('surname', null);
    } else if (
      ['M', 'F'].includes(formDataFlow.gender)
      && formDataFlow.date_of_birth === null
    ) {
      setValue('date_of_birth', '', triggerValidation);
      setValue('country_of_birth_code', 'Z000', triggerValidation);
      setValue('province_of_birth_code', null, triggerValidation);
      setValue('commune_of_birth_code', null, triggerValidation);
      setValue('born_abroad', false, triggerValidation);
      setValue('name', '', triggerValidation);
      setValue('surname', '', triggerValidation);
    }
  }, [formDataFlow.gender, formDataFlow.date_of_birth]);

  // On entering a step, mark that step's prefilled fields touched and validate
  // only them — so defaults show green borders without firing errors for
  // fields the user has not reached yet. Array fields (guarantees) are skipped:
  // the field array manages its own touched state.
  useEffect(() => {
    const values = getValues();
    if (values === undefined) {
      return;
    }

    const stepFields = answersFieldsBySteps[index - 1] || [];
    const filledKeys = stepFields.filter(
      (key) => values[key] !== ''
        && values[key] !== null
        && values[key] !== undefined
        && !Array.isArray(values[key]),
    );
    if (filledKeys.length === 0) {
      return;
    }
    filledKeys.forEach((key) => {
      setValue(key, values[key], { shouldTouch: true, shouldDirty: true });
    });
    trigger(filledKeys);
  }, [index]);

  // Keep step 1's collapsed "Detagli" panel open whenever any field inside it
  // is in an error state, so errors surfaced by "Continua" are never hidden.
  useEffect(() => {
    const panelFields = COLLAPSED_PANEL_FIELDS_BY_STEP[index] || [];
    if (!panelFields.some((field) => errors[field])) {
      return;
    }
    if (index === 1) setShowStepOneFields(true);
  }, [errors, index]);

  useEffect(() => {
    const callAsync = async () => {
      skipCheckGenderRef.current = true;
      const {
        data: {
          data: { userData },
        },
      } = await modifyRequestDataBackoffice(requestToken);

      setSkipBrandCodeEffect(true);
      setSkipModelCodeEffect(true);
      // Handle guarantees separately with field array replace
      if (userData.guarantees && Array.isArray(userData.guarantees)) {
        // Transform guarantees to ensure parameterValues is always an object
        const transformedGuarantees = userData.guarantees.map((guarantee) => ({
          ...guarantee,
          parameterValues: Array.isArray(guarantee.parameterValues)
            ? {}
            : (guarantee.parameterValues || {}),
        }));
        replace(transformedGuarantees);
        const { guarantees, ...restUserData } = userData;
        updateFormData(restUserData);
      } else {
        updateFormData(userData);
      }
    };

    callAsync();
  }, [location]);

  useEffect(() => {
    if (formDataFlow.imm_vehicle_year !== '') {
      setProceedButton(false);
    }
  }, [formDataFlow.imm_vehicle_year]);

  if (loading) {
    return <PageLoading />;
  }

  if (loadingQuotes) {
    return <QuoteGenerateLoading countNr={45} />;
  }
  return (
    <Layout>
      <V2FontImport />
      <div className="d-flex align-content-center justify-content-center">
        <ProgressBar activeStep={index} steps={stepsQuote} />
      </div>
      <AncillariAutoFormStyles className="w-50 mx-auto">
        {index === 1 && (
          <BackofficeAncillariStepOne
            formDataFlow={formDataFlow}
            updateFormData={updateFormData}
            register={register}
            errors={errors}
            isValid={isValid}
            vehicle_plate_types={vehicle_plate_types}
            brands={brands}
            vehicleModels={vehicleModels}
            isLoadingVehicleModels={isLoadingVehicleModels}
            vehicleVersions={vehicleVersions}
            isLoadingVehicleVersions={isLoadingVehicleVersions}
            km_during_one_year={km_during_one_year}
            vehiclesAmountInFamily={vehiclesAmountInFamily}
            formData={formData}
            showStepOneFields={showStepOneFields}
            setShowStepOneFields={setShowStepOneFields}
          />
        )}
        {index === 2 && (
          <BackofficeAncillariStepTwo
            fields={fields}
            replace={replace}
            update={update}
          />
        )}
        {index === 3 && (
          <BackofficeAncillariStepThree
            register={register}
            errors={errors}
            formDataFlow={formDataFlow}
            updateUserData={updateUserData}
            states={states}
            municipalities={municipalities}
            formData={formData}
            isValid={isValid}
            updateFormData={updateFormData}
            dataPrivacy={dataPrivacy}
            privacyItemChangeHandler={privacyItemChangeHandler}
            showPrivacyErrors={showPrivacyErrors}
          />
        )}

        {index >= 1 && index <= 4 && (
          <FooterNav className="footer-buttons">
            <div className="d-flex justify-content-between">
              {index !== 1 && (
                <SecondaryButton
                  type="button"
                  className="btn btn-questionnaire back backtoStepOne backtoStepTwo backtoStepThree"
                  onClick={prevButton}
                >
                  Indietro
                </SecondaryButton>
              )}

              {proceedButton === false && (
                <PrimaryButton
                  type="button"
                  className="btn btn-questionnaire continueStepThree continueStepFour buttonFinal"
                  onClick={nextButton}
                  id="continueStep2"
                >
                  {index === 3 ? 'Vai ai preventivi' : 'Continua'}
                </PrimaryButton>
              )}
            </div>
          </FooterNav>
        )}
      </AncillariAutoFormStyles>
    </Layout>
  );
}

export default BackOfficeAncillari;
